import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import {
  Bell,
  CheckCheck,
  MailOpen,
  Search,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { ApiError } from '../../../lib/api'
import { fetchNotifications, markAllNotificationsRead, markNotificationRead } from '../api'
import { getLocalizedNotification } from '../lib/notification-content'
import { PageHeader, PageHeaderToolbar } from '@/components/app/PageHeader'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { NotificationListItem } from '../components/NotificationListItem'
import type { AppNotificationRecord } from '../../../types/notifications'

const ALL_TYPES = [
  { value: 'all', labelKey: 'notifications.page.allTypes' },
  { value: 'agent', labelKey: 'notifications.page.types.agent' },
  { value: 'program', labelKey: 'notifications.page.types.program' },
  { value: 'business', labelKey: 'notifications.page.types.business' },
  { value: 'prospect', labelKey: 'notifications.page.types.prospect' },
  { value: 'exchange', labelKey: 'notifications.page.types.exchange' },
  { value: 'transaction', labelKey: 'notifications.page.types.transaction' },
  { value: 'sync', labelKey: 'notifications.page.types.sync' },
  { value: 'platform', labelKey: 'notifications.page.types.platform' },
]

function isToday(date: Date): boolean {
  const now = new Date()
  return (
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear()
  )
}

function isYesterday(date: Date): boolean {
  const now = new Date()
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  return (
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear()
  )
}

function isThisWeek(date: Date): boolean {
  const now = new Date()
  const startOfWeek = new Date(now)
  startOfWeek.setDate(now.getDate() - now.getDay())
  startOfWeek.setHours(0, 0, 0, 0)
  return date >= startOfWeek && !isToday(date) && !isYesterday(date)
}

function groupByDate(items: AppNotificationRecord[], t: (key: string) => string) {
  const groups: { label: string; items: AppNotificationRecord[] }[] = []
  const today: AppNotificationRecord[] = []
  const yesterday: AppNotificationRecord[] = []
  const thisWeek: AppNotificationRecord[] = []
  const earlier: AppNotificationRecord[] = []

  for (const item of items) {
    const date = new Date(item.created_at ?? 0)
    if (isToday(date)) today.push(item)
    else if (isYesterday(date)) yesterday.push(item)
    else if (isThisWeek(date)) thisWeek.push(item)
    else earlier.push(item)
  }

  if (today.length) groups.push({ label: t('notifications.page.today'), items: today })
  if (yesterday.length) groups.push({ label: t('notifications.page.yesterday'), items: yesterday })
  if (thisWeek.length) groups.push({ label: t('notifications.page.earlierThisWeek'), items: thisWeek })
  if (earlier.length) groups.push({ label: t('notifications.page.earlier'), items: earlier })

  return groups
}

function NotificationsPageSkeleton() {
  return (
    <section className="app-section">
      <div className="flex flex-col gap-2 pb-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
        <Skeleton className="h-6 w-32" />
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
          <Skeleton className="h-8 w-full sm:w-[260px]" />
          <Skeleton className="h-8 w-full sm:w-[140px]" />
          <Skeleton className="h-8 w-full sm:w-[140px]" />
          <Skeleton className="h-8 w-full sm:w-[130px]" />
        </div>
      </div>
      <div className="mt-4 space-y-6">
        {Array.from({ length: 3 }).map((_, gi) => (
          <div key={gi}>
            <Skeleton className="h-4 w-24 mb-2" />
            <div className="rounded-[0.625rem] overflow-hidden">
              <div className="flex flex-col gap-1">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="flex items-start gap-3 px-4 py-3.5 bg-card">
                    <Skeleton className="size-9 shrink-0 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-3 w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

export function NotificationsPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()

  const search = searchParams.get('q') ?? ''
  const typeFilter = searchParams.get('type') ?? 'all'
  const scope = searchParams.get('scope') === 'read' ? 'read' : 'unread'

  const listQuery = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: fetchNotifications,
  })

  const markOneMutation = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })

  const markAllMutation = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })

  const records = useMemo(() => listQuery.data?.data ?? [], [listQuery.data])
  const unreadCount = listQuery.data?.meta.unread_count ?? 0
  const readCount = records.filter((r) => r.read_at).length

  const filteredRecords = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase()

    return records
      .filter((item) => {
        const matchesScope =
          scope === 'read' ? item.read_at !== null : !item.read_at
        const matchesType =
          typeFilter === 'all' || item.notification_type === typeFilter
        const { title, message } =
          normalizedSearch.length === 0
            ? { title: '', message: '' }
            : getLocalizedNotification(item, t)
        const matchesSearch =
          normalizedSearch.length === 0 ||
          title.toLowerCase().includes(normalizedSearch) ||
          message.toLowerCase().includes(normalizedSearch)

        return matchesScope && matchesType && matchesSearch
      })
      .sort((a, b) => {
        return (
          new Date(b.created_at ?? 0).getTime() -
          new Date(a.created_at ?? 0).getTime()
        )
      })
  }, [records, search, typeFilter, scope, t])

  const dateGroups = useMemo(() => groupByDate(filteredRecords, t), [filteredRecords, t])

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams)
    if (value === 'all' || value === '') {
      next.delete(key)
    } else {
      next.set(key, value)
    }
    setSearchParams(next, { replace: true })
  }

  if (listQuery.isPending) {
    return <NotificationsPageSkeleton />
  }

  if (listQuery.isError) {
    return (
      <article className="rounded-lg border border-red-200 bg-red-50 p-6 text-sm text-red-700">
        {(listQuery.error as ApiError).message}
      </article>
    )
  }

  const isReadView = scope === 'read'

  return (
    <section className={cn('app-section', dateGroups.length === 0 && 'flex flex-col min-h-[calc(100dvh-4rem)]')}>
      {/* Header */}
      <PageHeader
        title={t('notifications.title')}
        right={
          <PageHeaderToolbar>
            {/* Search */}
            <div className="relative w-full sm:min-w-[220px] sm:max-w-[320px] sm:flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => updateParam('q', e.target.value)}
                placeholder={t('notifications.page.searchPlaceholder')}
                className="pl-9 h-8"
              />
            </div>

            {/* Type filter — clean, no left icon */}
            <Select
              value={typeFilter}
              onValueChange={(v) => updateParam('type', v)}
            >
              <SelectTrigger size="sm" className="w-full sm:w-auto sm:min-w-[130px]">
                <SelectValue placeholder={t('notifications.page.filterType')} />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>{t('notifications.page.filterType')}</SelectLabel>
                  {ALL_TYPES.map((tItem) => (
                    <SelectItem key={tItem.value} value={tItem.value}>
                      {t(tItem.labelKey)}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>

            {/* Mark all read */}
            {!isReadView && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={markAllMutation.isPending || unreadCount === 0}
                onClick={() => markAllMutation.mutate()}
                className="gap-1.5"
              >
                <CheckCheck className="size-4" />
                {t('notifications.page.markAllRead')}
              </Button>
            )}

            {/* Unread / Read tabs */}
            <Tabs
              value={scope}
              onValueChange={(value) => {
                const next = new URLSearchParams(searchParams)
                if (value === 'read') {
                  next.set('scope', 'read')
                } else {
                  next.delete('scope')
                }
                setSearchParams(next, { replace: true })
              }}
            >
              <TabsList>
                <TabsTrigger value="unread">
                  <Bell className="size-4" />
                  {t('notifications.page.unread')}
                  {unreadCount > 0 && (
                    <span className="ml-1.5 text-[10px] opacity-60">({unreadCount})</span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="read">
                  <MailOpen className="size-4" />
                  {t('notifications.page.readStatus')}
                  {readCount > 0 && (
                    <span className="ml-1.5 text-[10px] opacity-60">({readCount})</span>
                  )}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </PageHeaderToolbar>
        }
      />

      {/* Content */}
      {dateGroups.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center py-20 text-center">
          <img
            src="/Uploads/nitification_empty.webp"
            alt=""
            className="h-36 w-36 object-contain opacity-90"
          />
          <h3 className="mt-6 text-xl font-semibold tracking-tight text-foreground">
            {isReadView
              ? t('notifications.page.emptyReadTitle')
              : unreadCount === 0
                ? t('notifications.page.emptyUnreadTitle')
                : t('notifications.page.emptyFilterTitle')}
          </h3>
          <p className="mt-2 max-w-sm text-sm text-muted-foreground leading-relaxed">
            {isReadView
              ? t('notifications.page.emptyReadDesc')
              : unreadCount === 0
                ? t('notifications.page.emptyUnreadDesc')
                : t('notifications.page.emptyFilterDesc')}
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Button
              type="button"
              size="default"
              onClick={() => {
                const next = new URLSearchParams(searchParams)
                if (isReadView) {
                  next.delete('scope')
                } else if (unreadCount === 0) {
                  next.set('scope', 'read')
                } else {
                  next.delete('q')
                  next.delete('type')
                }
                setSearchParams(next, { replace: true })
              }}
            >
              {isReadView
                ? t('notifications.page.backToUnread')
                : unreadCount === 0
                  ? t('notifications.page.viewHistory')
                  : t('notifications.page.clearFilters')}
            </Button>
            <Button type="button" variant="outline" size="default" asChild>
              <Link to="/dashboard">{t('notifications.page.backToDashboard')}</Link>
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-4 space-y-6">
          {dateGroups.map((group) => (
            <div key={group.label}>
              <h3 className="mb-2 text-sm font-medium text-muted-foreground/80">
                {group.label}
              </h3>
              <div className="rounded-[0.625rem] overflow-hidden">
                <div className="flex flex-col gap-1">
                  {group.items.map((item) => (
                    <NotificationListItem
                      key={item.id}
                      item={item}
                      onMarkRead={
                        !item.read_at
                          ? () => markOneMutation.mutate(item.id)
                          : undefined
                      }
                      isBusy={markOneMutation.isPending}
                    />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}
